//React
import { useEffect } from 'react';
import {
  BrowserRouter,
  Outlet,
  Route,
  Routes,
  useNavigate,
} from 'react-router-dom';

//Refine
import type { I18nProvider } from '@refinedev/core';
import { Authenticated, Refine } from '@refinedev/core';
import { RefineKbar, RefineKbarProvider } from '@refinedev/kbar';
import { ErrorComponent } from '@refinedev/mantine';
import routerBindings, {
  DocumentTitleHandler,
  NavigateToResource,
  UnsavedChangesNotifier,
} from '@refinedev/react-router-v6';

//Mantine
import { MantineProvider } from '@mantine/core';
import { Notifications } from '@mantine/notifications';

//Mantine Styles
import '@mantine/core/styles.css';
import '@mantine/charts/styles.css';
import '@mantine/carousel/styles.css';
import '@mantine/dates/styles.css';
import '@mantine/dropzone/styles.css';
import '@mantine/notifications/styles.css';
import 'mantine-react-table/styles.css';

// Icons
import {
  IconHome,
  IconReportMedical,
  IconBookUpload,
  IconMoneybag,
  IconReportMoney,
  IconWallet,
  IconStethoscope,
  IconBuildingHospital,
  IconUsers,
  IconUsersGroup,
  IconNews,
  IconReceipt2,
  IconBuildingSkyscraper,
  IconUserExclamation,
  IconIdBadge2,
  IconFileDollar,
  IconFriends,
  IconChecklist,
  IconBuildingBank,
  IconPrinter,
  IconGavel,
  IconDatabaseSearch,
  IconUserSearch,
  IconTestPipe,
} from '@tabler/icons-react';

//Auth
import { authProvider } from './authProvider';

//i18n
import { useTranslation } from 'react-i18next';

// UI Components
import { Header } from './components/layout/header';
import { Layout } from './components/layout/index';
import { Sider } from './components/layout/sider';
import { Title } from './components/layout/title';
import { ChatManager } from './components/chat/ChatManager';

// Route Management
import {
  AdminManagementRoute,
  ClaimManagementRoute,
  DashboardManagementRoute,
  InvoiceManagementRoute,
  EOBManagementRoute,
  CreditManagementRoute,
  OrganizationManagementRoute,
  PatientManagementRoute,
  PayorManagementRoute,
  ProviderManagementRoute,
  QueryManagementRoute,
  RemittanceManagementRoute,
  ReportManagementRoute,
  RoleManagementRoute,
  SettingsManagementRoute,
  SiteManagementRoute,
  StatementManagementRoute,
  SubmissionManagementRoute,
  TariffManagementRoute,
  UploadManagementRoute,
  UserManagementRoute,
  LoginManagementRoute,
  ForgotPasswordManagementRoute,
  UpdatePasswordManagementRoute,
  VerifyManagementRoute,
  FaxManagementRoute,
  RuleManagementRoute,
  AdminProviderDashboardRoute,
  IngestTestingManagementRoute,
} from 'components/routes';

// Query
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';

// Devtools
import { ReactQueryDevtools } from '@tanstack/react-query-devtools';

import { notificationProvider } from 'utils/notificationProvider';

import { LocalProdBanner } from 'components/common/LocalProdBanner';
import { StagingBanner } from 'components/common/StagingBanner';
import { LoadingPage } from 'components/common/LoadingPage';
import { useGlobalHotkeys } from 'hooks/useGlobalHotkeys';
import { theme } from './styles/theme';
import { sendWebSocketPing } from 'api/ws/ping';
import { useCurrentUserStore } from 'store/UserStore';
import { CheatSheetProvider } from 'constants/CheatSheetContext';
import {
  SettingsSyncManager,
  SettingsUnloadSync,
} from 'components/settings/SettingsSyncManager';
import { initFirebaseApp } from './firebase';
import { NotificationSyncManager } from 'components/notifications/NotificationSyncManager';
import { NotificationCenterPage } from 'pages/notifications';
import { isIngestTestingAvailable } from 'utils/ingestTesting';

const queryClient = new QueryClient({
  defaultOptions: {
    mutations: {
      retry: 0,
    },
    queries: {
      refetchOnWindowFocus: false,
      refetchOnMount: true,
      retry: 0,
    },
  },
});

const HotkeysWrapper: React.FC = () => {
  const navigate = useNavigate();
  useGlobalHotkeys(navigate);
  return null;
};

function App() {
  const { t, i18n } = useTranslation();
  const i18nProvider: I18nProvider = {
    translate: (key: string, options?: any) => t(key, options) as string,
    changeLocale: (lang: string) => i18n.changeLanguage(lang),
    getLocale: () => i18n.language,
  };
  const iconSize = 20;
  const { cognitoID, organizationID } = useCurrentUserStore((state) => ({
    cognitoID: state.cognitoID,
    organizationID: state.organizationID,
  }));

  useEffect(() => {
    if (!cognitoID || typeof window === 'undefined') return;

    const ping = async () =>
      await sendWebSocketPing(cognitoID, organizationID || undefined);

    ping();

    const intervalID = window.setInterval(ping, 60_000);

    return () => window.clearInterval(intervalID);
  }, [cognitoID, organizationID]);

  useEffect(() => {
    initFirebaseApp();
  }, []);

  return (
    <QueryClientProvider client={queryClient}>
      <BrowserRouter basename="">
        <RefineKbarProvider>
          <MantineProvider theme={theme} defaultColorScheme="auto">
            <StagingBanner />
            <LocalProdBanner />
            <Refine
              notificationProvider={notificationProvider}
              routerProvider={routerBindings}
              authProvider={authProvider}
              i18nProvider={i18nProvider}
              resources={[
                {
                  name: 'dashboard',
                  list: '/',
                  meta: {
                    label: t('pages:dashboard.label'),
                    icon: <IconHome size={iconSize} />,
                  },
                },
                {
                  name: 'uploads',
                  identifier: 'uploads',
                  list: '/uploads',
                  edit: '/uploads/edit/:id',
                  meta: {
                    label: t('pages:uploads.label'),
                    icon: <IconChecklist size={iconSize} />,
                  },
                },
                {
                  name: 'claims',
                  identifier: 'claims',
                  list: '/claims',
                  edit: '/claims/edit/:id',
                  meta: {
                    label: t('pages:claims.label'),
                    icon: <IconReportMedical size={iconSize} />,
                  },
                },
                {
                  name: 'queries',
                  list: '/queries',
                  meta: {
                    label: t('pages:queries.label'),
                    icon: <IconDatabaseSearch size={iconSize} />,
                  },
                },
                {
                  name: 'submissions',
                  list: '/submissions',
                  meta: {
                    label: t('pages:submissions.label'),
                    icon: <IconBookUpload size={iconSize} />,
                  },
                },
                {
                  name: 'remittances',
                  list: '/remittances',
                  meta: {
                    label: t('pages:remittances.label'),
                    icon: <IconMoneybag size={iconSize} />,
                  },
                },
                {
                  name: 'credits',
                  list: '/credits',
                  meta: {
                    label: t('pages:credits.label'),
                    icon: <IconReportMoney size={iconSize} />,
                  },
                },
                {
                  name: 'statements',
                  list: '/statements',
                  meta: {
                    label: t('pages:statements.label'),
                    icon: <IconWallet size={iconSize} />,
                  },
                },
                {
                  name: 'providers',
                  list: '/providers',
                  meta: {
                    label: t('pages:providers.label'),
                    icon: <IconStethoscope size={iconSize} />,
                  },
                },
                {
                  name: 'provider-dashboard',
                  list: '/provider-dashboard',
                  meta: {
                    label: t('pages:providerDashboard.admin.label'),
                    icon: <IconUserSearch size={iconSize} />,
                  },
                },
                {
                  name: 'sites',
                  list: '/sites',
                  meta: {
                    label: t('pages:sites.label'),
                    icon: <IconBuildingHospital size={iconSize} />,
                  },
                },
                {
                  name: 'users',
                  list: '/users',
                  meta: {
                    label: t('pages:users.label'),
                    icon: <IconUsers size={iconSize} />,
                  },
                },
                {
                  name: 'roles',
                  list: '/roles',
                  meta: {
                    label: t('pages:roles.label'),
                    icon: <IconUsersGroup size={iconSize} />,
                  },
                },
                {
                  name: 'reports',
                  list: '/reports',
                  meta: {
                    label: t('pages:reports.label'),
                    icon: <IconNews size={iconSize} />,
                    canDelete: true,
                  },
                },
                {
                  name: 'tariffs',
                  list: '/tariffs',
                  meta: {
                    label: t('pages:tariffs.label'),
                    icon: <IconReceipt2 size={iconSize} />,
                  },
                },
                {
                  name: 'organizations',
                  list: '/organizations',
                  meta: {
                    label: t('pages:organizations.label'),
                    icon: <IconBuildingSkyscraper size={iconSize} />,
                  },
                },
                {
                  name: 'admins',
                  list: '/admins',
                  meta: {
                    label: t('pages:admins.label'),
                    icon: <IconUserExclamation size={iconSize} />,
                  },
                },
                {
                  name: 'eobs',
                  list: '/eob',
                  meta: {
                    label: t('pages:eobs.label'),
                    icon: <IconIdBadge2 size={iconSize} />,
                  },
                },
                {
                  name: 'invoices',
                  list: '/invoices',
                  meta: {
                    label: t('pages:invoices.label'),
                    icon: <IconFileDollar size={iconSize} />,
                  },
                },
                {
                  name: 'patients',
                  list: '/patients',
                  meta: {
                    label: t('pages:patients.label'),
                    icon: <IconFriends size={iconSize} />,
                  },
                },
                {
                  name: 'payors',
                  list: '/payors',
                  meta: {
                    label: t('pages:payors.label'),
                    icon: <IconBuildingBank size={iconSize} />,
                  },
                },
                {
                  name: 'faxes',
                  list: '/faxes',
                  meta: {
                    label: t('pages:faxes.label'),
                    icon: <IconPrinter size={iconSize} />,
                  },
                },
                {
                  name: 'rules',
                  list: '/rules',
                  meta: {
                    label: t('pages:rules.label'),
                    icon: <IconGavel size={iconSize} />,
                  },
                },
                ...(isIngestTestingAvailable()
                  ? [
                      {
                        name: 'ingest-testing',
                        list: '/ingest-testing',
                        meta: {
                          label: t('pages:ingestTesting.label'),
                          icon: <IconTestPipe size={iconSize} />,
                        },
                      },
                    ]
                  : []),
              ]}
              options={{
                syncWithLocation: true,
                warnWhenUnsavedChanges: true,
                disableTelemetry: true,
              }}
            >
              <Notifications position="top-right" zIndex={500} />
              <HotkeysWrapper />
              <CheatSheetProvider>
                <Routes>
                  <Route
                    element={
                      <Authenticated
                        redirectOnFail="/login"
                        key="authenticated"
                        loading={<LoadingPage />}
                      >
                        <Layout
                          Header={Header}
                          Sider={Sider}
                          Title={Title}
                          OffLayoutArea={ChatManager}
                          initialSiderCollapsed={false}
                        >
                          {/* Settings sync (hydrate + background sync) */}
                          <SettingsUnloadSync />
                          <SettingsSyncManager />
                          <NotificationSyncManager />
                          <Outlet />
                        </Layout>
                      </Authenticated>
                    }
                  >
                    <Route index element={<DashboardManagementRoute />} />
                    <Route path="/claims">
                      <Route index element={<ClaimManagementRoute />} />
                      <Route
                        path="edit/:id"
                        element={<ClaimManagementRoute />}
                      />
                    </Route>
                    <Route path="/queries" element={<QueryManagementRoute />} />
                    <Route path="/faxes" element={<FaxManagementRoute />} />
                    <Route path="/uploads">
                      <Route index element={<UploadManagementRoute />} />
                      <Route
                        path="edit/:id"
                        element={<UploadManagementRoute />}
                      />
                    </Route>
                    <Route
                      path="/submissions"
                      element={<SubmissionManagementRoute />}
                    />
                    <Route path="/roles" element={<RoleManagementRoute />} />
                    <Route path="/sites" element={<SiteManagementRoute />} />
                    <Route path="/users" element={<UserManagementRoute />} />
                    <Route
                      path="/providers"
                      element={<ProviderManagementRoute />}
                    />
                    <Route
                      path="/provider-dashboard"
                      element={<AdminProviderDashboardRoute />}
                    />
                    <Route
                      path="/reports"
                      element={<ReportManagementRoute />}
                    />
                    <Route
                      path="/notifications"
                      element={<NotificationCenterPage />}
                    />
                    <Route
                      path="/tariffs"
                      element={<TariffManagementRoute />}
                    />
                    <Route
                      path="/organizations"
                      element={<OrganizationManagementRoute />}
                    />
                    <Route path="/admins" element={<AdminManagementRoute />} />
                    <Route
                      path="/remittances"
                      element={<RemittanceManagementRoute />}
                    />
                    <Route path="/eobs" element={<EOBManagementRoute />} />
                    <Route
                      path="/credits"
                      element={<CreditManagementRoute />}
                    />
                    <Route
                      path="/patients"
                      element={<PatientManagementRoute />}
                    />
                    <Route path="/payors" element={<PayorManagementRoute />} />
                    <Route
                      path="/statements"
                      element={<StatementManagementRoute />}
                    />
                    <Route
                      path="/invoices"
                      element={<InvoiceManagementRoute />}
                    />
                    <Route path="/rules" element={<RuleManagementRoute />} />
                    <Route
                      path="/ingest-testing"
                      element={<IngestTestingManagementRoute />}
                    />
                    <Route
                      path="/settings"
                      element={<SettingsManagementRoute />}
                    />
                    <Route path="*" element={<ErrorComponent />} />
                  </Route>
                  <Route
                    element={
                      <Authenticated
                        fallback={<Outlet />}
                        loading={<LoadingPage />}
                        key="unauthenticated"
                      >
                        <NavigateToResource />
                      </Authenticated>
                    }
                  >
                    <Route path="/login" element={<LoginManagementRoute />} />
                    <Route
                      path="/forgot-password"
                      element={<ForgotPasswordManagementRoute />}
                    />
                    <Route
                      path="/update-password"
                      element={<UpdatePasswordManagementRoute />}
                    />
                    <Route path="/verify" element={<VerifyManagementRoute />} />
                  </Route>
                </Routes>
                <UnsavedChangesNotifier />
              </CheatSheetProvider>
              <RefineKbar />
              <DocumentTitleHandler />
              <ReactQueryDevtools
                initialIsOpen={false}
                buttonPosition="bottom-left"
                position="right"
              />
            </Refine>
          </MantineProvider>
        </RefineKbarProvider>
      </BrowserRouter>
    </QueryClientProvider>
  );
}

export default App;
